import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { api } from "../lib/api";
import SEO from "../components/SEO";

export default function AdminLogin() {
    const { login } = useAuth();
    const { logoUrl } = useBranding();
    const nav = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("admin@mystreetz.mag");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSending, setForgotSending] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    useEffect(() => {
        if (location.state?.authMessage) {
            toast(location.state.authMessage, { duration: 7000 });
            nav(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signInWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back, editor.");
            nav("/admin");
        } catch (err) {
            toast.error("Invalid credentials.");
        } finally {
            setLoading(false);
        }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail) return toast.error("Enter your email.");
        setForgotSending(true);
        try {
            await api.post("/auth/forgot-password", { email: forgotEmail });
            setForgotSent(true);
        } catch (err) {
            // Backend deliberately doesn't leak existence, but Resend / validation errors can surface here
            toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send reset email.");
        } finally {
            setForgotSending(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen grid place-items-center px-4">
            <SEO title="Editor Login" noindex />
            <div className="w-full max-w-md">
                <Link to="/" className="flex justify-center mb-8" data-testid="admin-logo-link">
                    <img src={logoUrl} alt="MyStreetz" className="brand-logo h-10" />
                </Link>
                <form
                    onSubmit={onSubmit}
                    data-testid="admin-login-form"
                    className="bg-streetz-paper border border-streetz-line p-8 flex flex-col gap-4"
                >
                    <h1 className="font-display uppercase text-3xl">Editor login</h1>
                    <label className="flex flex-col gap-1">
                        <span className="overline">Email</span>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="admin-login-email"
                            className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline">Password</span>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-login-password"
                            className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="bg-streetz-red text-white overline py-3 hover:bg-white hover:text-black disabled:opacity-60"
                    >
                        {loading ? "SIGNING IN..." : "SIGN IN"}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotSent(false); }}
                        data-testid="admin-forgot-password-link"
                        className="overline text-streetz-muted hover:text-streetz-yellow text-center text-xs"
                    >
                        Forgot your password?
                    </button>

                    <div className="flex items-center gap-3 my-1">
                        <div className="h-px flex-1 bg-streetz-line" />
                        <span className="overline text-streetz-muted text-xs">OR</span>
                        <div className="h-px flex-1 bg-streetz-line" />
                    </div>
                    <button
                        type="button"
                        onClick={signInWithGoogle}
                        data-testid="google-signin-button"
                        className="flex items-center justify-center gap-3 border border-streetz-line bg-white text-black overline py-3 hover:bg-streetz-line hover:text-white transition-colors"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                        </svg>
                        Continue with Google
                    </button>
                    <p className="text-streetz-muted text-xs text-center">
                        Team access only. New Google accounts are held for admin approval.
                    </p>
                </form>
            </div>

            {forgotOpen && (
                <div
                    onClick={() => setForgotOpen(false)}
                    data-testid="forgot-password-modal"
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4"
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submitForgot}
                        className="bg-streetz-paper border border-streetz-line p-8 flex flex-col gap-4 w-full max-w-md"
                        data-testid="forgot-password-form"
                    >
                        <h2 className="font-display uppercase text-2xl">Reset your password</h2>
                        {forgotSent ? (
                            <>
                                <p className="text-sm text-streetz-muted" data-testid="forgot-password-sent">
                                    If an account with <strong>{forgotEmail}</strong> exists, a reset link has been emailed.
                                    Check your inbox (and spam folder). The link expires in 60 minutes.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setForgotOpen(false)}
                                    data-testid="forgot-password-close"
                                    className="overline bg-streetz-red text-white py-2 hover:bg-white hover:text-black"
                                >
                                    GOT IT
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-streetz-muted">
                                    Enter the email on your editor account. We&apos;ll email you a single-use reset link.
                                </p>
                                <label className="flex flex-col gap-1">
                                    <span className="overline">Email</span>
                                    <input
                                        type="email"
                                        required
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        data-testid="forgot-password-email"
                                        className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none"
                                    />
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setForgotOpen(false)}
                                        className="overline border border-streetz-line px-4 py-2 hover:border-streetz-red flex-1"
                                        data-testid="forgot-password-cancel"
                                    >
                                        CANCEL
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={forgotSending}
                                        data-testid="forgot-password-submit"
                                        className="overline bg-streetz-red text-white px-4 py-2 hover:bg-white hover:text-black disabled:opacity-60 flex-1"
                                    >
                                        {forgotSending ? "SENDING…" : "SEND RESET LINK"}
                                    </button>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            )}
        </div>
    );
}
